import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, ChatCircle, Trash, PaperPlaneRight, X, PencilSimpleLine, Robot, Wallet, Sparkle, ChartLine, BookOpen, UserCirclePlus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { formatCurrency } from '../lib/utils';
import RankBadge from '../components/RankBadge';
import TeenVestLoader from '../components/TeenVestLoader';

const POST_TYPE_META = {
  text: { label: 'Post', icon: Sparkle, color: 'text-blue-300' },
  blog: { label: 'Blog', icon: BookOpen, color: 'text-purple-300' },
  portfolio: { label: 'Portfolio Share', icon: ChartLine, color: 'text-emerald-300' },
  intro: { label: 'Intro', icon: UserCirclePlus, color: 'text-yellow-300' },
  bot_news: { label: 'MarketBot News', icon: Robot, color: 'text-cyan-300' },
  bot_take: { label: 'MarketBot Hot Take', icon: Robot, color: 'text-pink-300' },
  bot_leaderboard: { label: 'Leaderboard Spotlight', icon: Robot, color: 'text-amber-300' },
};

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  } catch {
    return '';
  }
};

const AuthorChip = ({ post, onClick }) => {
  const initials = (post.author_username || '?').slice(0, 2).toUpperCase();
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 group text-left"
      data-testid={`post-author-${post.id}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
        post.is_bot
          ? 'bg-gradient-to-br from-cyan-400 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
          : 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black'
      }`}>
        {post.is_bot ? '🤖' : initials}
      </div>
      <div className="leading-tight">
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-white group-hover:text-gold transition-colors">
            {post.author_nickname || post.author_username}
          </span>
          {!post.is_bot && post.author_rank && (
            <RankBadge rank={post.author_rank} size="sm" />
          )}
          {post.is_bot && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-semibold uppercase tracking-wide">BOT</span>
          )}
        </div>
        <span className="text-xs text-zinc-500">@{post.author_username} · {formatRelative(post.created_at)}</span>
      </div>
    </button>
  );
};

const PortfolioSnapshotCard = ({ snapshot }) => {
  if (!snapshot) return null;
  const profitColor = snapshot.trading_profit >= 0 ? 'text-emerald-400' : 'text-red-400';
  return (
    <div className="mt-3 rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/40 to-zinc-900/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold">
          <ChartLine size={16} weight="fill" /> Portfolio Snapshot
        </div>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">{snapshot.holdings_count} holdings</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><p className="text-[10px] uppercase text-zinc-500">Total Value</p><p className="font-mono font-bold text-white">{formatCurrency(snapshot.total_value)}</p></div>
        <div><p className="text-[10px] uppercase text-zinc-500">Cash</p><p className="font-mono text-zinc-200">{formatCurrency(snapshot.cash)}</p></div>
        <div><p className="text-[10px] uppercase text-zinc-500">Trading P/L</p><p className={`font-mono font-bold ${profitColor}`}>{formatCurrency(snapshot.trading_profit)}</p></div>
        <div><p className="text-[10px] uppercase text-zinc-500">P/L %</p><p className={`font-mono font-bold ${profitColor}`}>{snapshot.trading_profit_percent?.toFixed(2)}%</p></div>
      </div>
      {snapshot.top_holdings?.length > 0 && (
        <div className="mt-3 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase text-zinc-500 mb-2">Top Holdings</p>
          <div className="space-y-1.5">
            {snapshot.top_holdings.map((h) => (
              <div key={h.symbol} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono font-bold text-white">{h.symbol}</span>
                  <span className="text-zinc-500 truncate">{h.company_name}</span>
                </div>
                <div className="flex items-center gap-3 text-right shrink-0">
                  <span className="font-mono text-zinc-300">{h.shares.toLocaleString()} sh</span>
                  <span className={`font-mono font-semibold ${h.profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {h.profit_loss_percent >= 0 ? '+' : ''}{h.profit_loss_percent.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CommentRow = ({ comment, isMine, onDelete }) => (
  <div className="flex items-start gap-2 py-2" data-testid={`comment-${comment.id}`}>
    <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[11px] font-bold text-zinc-200 flex-shrink-0">
      {(comment.author_username || '?').slice(0, 1).toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2 text-xs">
        <span className="font-semibold text-white">{comment.author_nickname || comment.author_username}</span>
        <span className="text-zinc-500">{formatRelative(comment.created_at)}</span>
      </div>
      <p className="text-sm text-zinc-200 break-words">{comment.content}</p>
    </div>
    {isMine && (
      <button onClick={onDelete} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-comment-${comment.id}`}>
        <Trash size={14} />
      </button>
    )}
  </div>
);

const PostCard = ({ post, currentUserId, onLike, onComment, onDeleteComment, onDeletePost, onAuthorClick }) => {
  const meta = POST_TYPE_META[post.type] || POST_TYPE_META.text;
  const Icon = meta.icon;
  const [showComments, setShowComments] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isMine = post.author_id === currentUserId;

  const submitComment = async (e) => {
    e?.preventDefault?.();
    if (!commentDraft.trim()) return;
    setSubmitting(true);
    const ok = await onComment(post.id, commentDraft.trim());
    if (ok) setCommentDraft('');
    setSubmitting(false);
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5"
      data-testid={`post-card-${post.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <AuthorChip post={post} onClick={() => onAuthorClick(post.author_username, post.is_bot)} />
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-[10px] uppercase tracking-wide ${meta.color}`}>
            <Icon size={12} weight="bold" /> {meta.label}
          </span>
          {isMine && !post.is_bot && (
            <button
              onClick={() => onDeletePost(post.id)}
              className="text-zinc-500 hover:text-red-400 transition-colors"
              data-testid={`delete-post-${post.id}`}
            >
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>

      {post.title && (
        <h3 className="text-lg md:text-xl font-bold text-white mb-2 leading-snug">{post.title}</h3>
      )}
      <p className="text-zinc-200 whitespace-pre-wrap leading-relaxed break-words">{post.content}</p>

      {post.type === 'portfolio' && <PortfolioSnapshotCard snapshot={post.portfolio_snapshot} />}

      {/* Actions */}
      <div className="flex items-center gap-5 mt-4 pt-3 border-t border-white/5">
        <button
          onClick={() => onLike(post.id)}
          className={`flex items-center gap-1.5 text-sm transition-colors ${post.liked_by_me ? 'text-red-400' : 'text-zinc-400 hover:text-red-300'}`}
          data-testid={`like-button-${post.id}`}
        >
          <Heart size={18} weight={post.liked_by_me ? 'fill' : 'regular'} />
          <span className="font-mono">{post.like_count}</span>
        </button>
        <button
          onClick={() => setShowComments((s) => !s)}
          className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-blue-300 transition-colors"
          data-testid={`comment-button-${post.id}`}
        >
          <ChatCircle size={18} weight="regular" />
          <span className="font-mono">{post.comment_count}</span>
        </button>
      </div>

      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mt-2"
          >
            <div className="border-t border-white/5 pt-2">
              {(post.comments || []).map((c) => (
                <CommentRow
                  key={c.id}
                  comment={c}
                  isMine={c.author_id === currentUserId}
                  onDelete={() => onDeleteComment(post.id, c.id)}
                />
              ))}
              {(post.comments || []).length === 0 && (
                <p className="text-xs text-zinc-500 py-2">No comments yet — be the first to chime in.</p>
              )}
              <form onSubmit={submitComment} className="flex items-center gap-2 mt-3" data-testid={`comment-form-${post.id}`}>
                <Input
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Write a comment..."
                  className="bg-black/40 border-white/10"
                  maxLength={1000}
                  data-testid={`comment-input-${post.id}`}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!commentDraft.trim() || submitting}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black"
                  data-testid={`comment-submit-${post.id}`}
                >
                  <PaperPlaneRight size={16} weight="fill" />
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};

const COMPOSER_TYPES = [
  { key: 'text', label: 'Post', icon: Sparkle, hint: 'Quick thought or update' },
  { key: 'blog', label: 'Blog', icon: BookOpen, hint: 'Longer-form article' },
  { key: 'portfolio', label: 'Share Portfolio', icon: ChartLine, hint: 'Snapshot your holdings' },
];

const Composer = ({ open, onClose, onSubmit, defaultType = 'text' }) => {
  const [type, setType] = useState(defaultType);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (type !== 'portfolio' && !content.trim()) {
      toast.error('Write something before posting');
      return;
    }
    setSubmitting(true);
    const payload = {
      type,
      content: type === 'portfolio' && !content.trim() ? 'Sharing my portfolio snapshot — let me know what you think!' : content.trim(),
    };
    if (type === 'blog' && title.trim()) payload.title = title.trim();
    const ok = await onSubmit(payload);
    setSubmitting(false);
    if (ok) onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="composer-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-xl bg-zinc-950 border border-white/10 rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">Create a post</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="composer-close">
            <X size={20} />
          </button>
        </div>
        <div className="flex gap-2 mb-4">
          {COMPOSER_TYPES.map((t) => {
            const Icon = t.icon;
            const active = type === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                className={`flex-1 px-3 py-2.5 rounded-xl border text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  active
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black border-transparent'
                    : 'bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10'
                }`}
                data-testid={`composer-type-${t.key}`}
              >
                <Icon size={16} weight={active ? 'fill' : 'regular'} /> {t.label}
              </button>
            );
          })}
        </div>
        <form onSubmit={submit} className="space-y-3">
          {type === 'blog' && (
            <Input
              placeholder="Blog title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              className="bg-black/40 border-white/10"
              data-testid="composer-title"
            />
          )}
          <Textarea
            placeholder={
              type === 'portfolio'
                ? 'Optional caption — what do you want people to notice about your portfolio?'
                : type === 'blog'
                ? 'Write your full article here. Plain text works great.'
                : "What's on your mind, investor?"
            }
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={type === 'blog' ? 10 : 5}
            maxLength={5000}
            className="bg-black/40 border-white/10 resize-y min-h-[120px]"
            data-testid="composer-content"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500">{content.length}/5000 · {COMPOSER_TYPES.find(c => c.key === type)?.hint}</span>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full px-6"
              data-testid="composer-submit"
            >
              {submitting ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const IntroGate = ({ onSubmit }) => {
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (draft.trim().length < 20) {
      toast.error('Tell us at least a sentence or two!');
      return;
    }
    setSubmitting(true);
    const ok = await onSubmit({ type: 'intro', content: draft.trim() });
    setSubmitting(false);
    if (!ok) return;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto glass-card p-8"
      data-testid="intro-gate"
    >
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 text-black mb-4">
          <UserCirclePlus size={32} weight="fill" />
        </div>
        <h2 className="text-2xl md:text-3xl font-unbounded font-bold text-white mb-2">Introduce yourself first 👋</h2>
        <p className="text-zinc-400">
          Before you can see the community feed, share a quick intro about your TeenVest profile —
          your investing style, favorite stock, or what you&apos;re learning.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Hi! I'm @yourname. I love tech stocks, especially NVDA, and I'm learning about long-term investing thanks to TeenVest's Buffett mode!"
          rows={5}
          maxLength={5000}
          className="bg-black/40 border-white/10 min-h-[140px]"
          data-testid="intro-textarea"
        />
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">{draft.length}/5000 · Min 20 characters</span>
        </div>
        <Button
          type="submit"
          disabled={submitting || draft.trim().length < 20}
          className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full py-6 text-base"
          data-testid="intro-submit"
        >
          {submitting ? 'Posting your intro...' : 'Post Intro & Enter the Feed'}
        </Button>
      </form>
    </motion.div>
  );
};

const Community = () => {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [composerType, setComposerType] = useState('text');
  const [introRequired, setIntroRequired] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadFeed = useCallback(async (replace = true) => {
    try {
      const skip = replace ? 0 : feed.length;
      const res = await api.get(`/community/feed?limit=20&skip=${skip}`);
      const next = res.data.posts || [];
      setFeed((prev) => (replace ? next : [...prev, ...next]));
      setHasMore((skip + next.length) < (res.data.total || 0));
    } catch (err) {
      toast.error('Could not load community feed');
    } finally {
      setLoading(false);
    }
  }, [feed.length]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const status = await api.get('/community/intro-status');
        if (cancelled) return;
        if (status.data.intro_required) {
          setIntroRequired(true);
          setLoading(false);
          return;
        }
        await loadFeed(true);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          toast.error('Sign up free to access the community feed');
          navigate('/dashboard');
          return;
        }
        toast.error('Community is unavailable right now');
        setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const handleSubmitPost = async (payload) => {
    try {
      const res = await api.post('/community/posts', payload);
      if (payload.type === 'intro') {
        setIntroRequired(false);
        await fetchUser?.();
        await loadFeed(true);
        toast.success('Welcome to the TeenVest community 🎉');
        return true;
      }
      setFeed((prev) => [res.data.post, ...prev]);
      toast.success('Posted!');
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not create post';
      toast.error(typeof msg === 'string' ? msg : 'Could not create post');
      return false;
    }
  };

  const handleLike = async (postId) => {
    setFeed((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      const liked = !p.liked_by_me;
      return { ...p, liked_by_me: liked, like_count: Math.max(0, p.like_count + (liked ? 1 : -1)) };
    }));
    try {
      await api.post(`/community/posts/${postId}/like`);
    } catch (err) {
      // revert
      setFeed((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        const liked = !p.liked_by_me;
        return { ...p, liked_by_me: liked, like_count: Math.max(0, p.like_count + (liked ? 1 : -1)) };
      }));
      toast.error('Could not toggle like');
    }
  };

  const handleComment = async (postId, content) => {
    try {
      const res = await api.post(`/community/posts/${postId}/comments`, { content });
      const comment = res.data.comment;
      setFeed((prev) => prev.map((p) => p.id === postId
        ? { ...p, comments: [...(p.comments || []), comment], comment_count: (p.comment_count || 0) + 1 }
        : p));
      return true;
    } catch (err) {
      toast.error('Could not post comment');
      return false;
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      await api.delete(`/community/posts/${postId}/comments/${commentId}`);
      setFeed((prev) => prev.map((p) => p.id === postId
        ? { ...p, comments: (p.comments || []).filter((c) => c.id !== commentId), comment_count: Math.max(0, (p.comment_count || 0) - 1) }
        : p));
    } catch {
      toast.error('Could not delete comment');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/community/posts/${postId}`);
      setFeed((prev) => prev.filter((p) => p.id !== postId));
      toast.success('Post deleted');
    } catch {
      toast.error('Could not delete post');
    }
  };

  const handleAuthorClick = (username, isBot) => {
    if (isBot) return;
    navigate('/leaderboard');
  };

  const loadMore = async () => {
    setLoadingMore(true);
    await loadFeed(false);
    setLoadingMore(false);
  };

  const openComposer = (type) => {
    setComposerType(type);
    setShowComposer(true);
  };

  return (
    <div className="min-h-screen noise-bg" data-testid="community-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl md:text-4xl font-unbounded font-bold text-white">Community</h1>
          <p className="text-zinc-400 mt-1">Share posts, blogs &amp; portfolio brags. Roast bad takes. Learn together.</p>
        </motion.div>

        {loading ? (
          <div className="text-center py-20">
            <TeenVestLoader size={96} label="Loading feed..." />
          </div>
        ) : introRequired ? (
          <IntroGate onSubmit={handleSubmitPost} />
        ) : (
          <>
            {/* Compose card */}
            <div className="glass-card p-4 mb-6" data-testid="compose-row">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 text-black flex items-center justify-center font-bold text-sm">
                  {(user?.username || '?').slice(0, 2).toUpperCase()}
                </div>
                <button
                  onClick={() => openComposer('text')}
                  className="flex-1 text-left px-4 py-2.5 rounded-full bg-black/40 border border-white/10 text-zinc-500 hover:text-zinc-300 transition-colors"
                  data-testid="compose-trigger"
                >
                  What&apos;s your hot take today, {user?.nickname || user?.username}?
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => openComposer('text')}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-zinc-200"
                  data-testid="compose-text"
                >
                  <Sparkle size={16} weight="fill" className="text-blue-300" /> Post
                </button>
                <button
                  onClick={() => openComposer('blog')}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-zinc-200"
                  data-testid="compose-blog"
                >
                  <BookOpen size={16} weight="fill" className="text-purple-300" /> Blog
                </button>
                <button
                  onClick={() => openComposer('portfolio')}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-sm text-emerald-200"
                  data-testid="compose-portfolio"
                >
                  <ChartLine size={16} weight="fill" /> Share Portfolio
                </button>
              </div>
            </div>

            {/* Feed */}
            <div className="space-y-4">
              {feed.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  currentUserId={user?.id}
                  onLike={handleLike}
                  onComment={handleComment}
                  onDeleteComment={handleDeleteComment}
                  onDeletePost={handleDeletePost}
                  onAuthorClick={handleAuthorClick}
                />
              ))}
              {feed.length === 0 && (
                <div className="text-center text-zinc-500 py-12">
                  <p>The feed is empty. Be the first to post!</p>
                </div>
              )}
              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    variant="ghost"
                    className="text-zinc-300 border border-white/10"
                    data-testid="load-more-button"
                  >
                    {loadingMore ? 'Loading...' : 'Load more posts'}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Composer
        key={composerType + (showComposer ? '-open' : '-closed')}
        open={showComposer}
        onClose={() => setShowComposer(false)}
        onSubmit={handleSubmitPost}
        defaultType={composerType}
      />
    </div>
  );
};

export default Community;
